// Retry transient failures with exponential backoff + jitter.
//
// Ported from v2_firebase_functions/functions/util/retryHelper.js, which is the
// canonical copy. The logic is deliberately kept the same so the two behave
// alike; the only change is console in place of the firebase-functions logger,
// since this runs in a browser.
//
// Why Send Rewards needs it: a rate-limited reward service answers 429, and the
// screen used to record that as a permanent failure for that fan. A 429 means
// "later", not "no".

export function isRetryableError(error) {
    // Network errors
    if (error.code === 'ECONNRESET' || error.code === 'ETIMEDOUT' ||
        error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
        return true;
    }

    // HTTP status codes
    const status = error.status || error.statusCode;
    if (status) {
        if (status === 429 || (status >= 500 && status < 600)) {
            return true;
        }
        // A 4xx is the request's own fault; sending it again changes nothing.
        if (status >= 400 && status < 500) {
            return false;
        }
    }

    // Common transient error messages
    const message = (error.message || '').toLowerCase();
    if (message.includes('timeout') || message.includes('rate limit') ||
        message.includes('too many requests') || message.includes('temporarily unavailable')) {
        return true;
    }

    return false;
}

/**
 * @param operation            async function to retry
 * @param maxRetries           attempts AFTER the first (default 3)
 * @param initialDelayMs       first backoff, doubled each attempt
 * @param maxDelayMs           cap on a single wait
 * @param shouldRetry          predicate; defaults to isRetryableError
 * @param operationName        label for the warning log
 */
export async function retryWithBackoff(operation, options = {}) {
    const {
        maxRetries = 3,
        initialDelayMs = 1000,
        maxDelayMs = 30000,
        shouldRetry = isRetryableError,
        operationName = 'operation',
        sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
    } = options;

    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;

            if (attempt >= maxRetries || !shouldRetry(error)) {
                throw error;
            }

            // Jitter keeps a room full of retries from lining up on the same tick.
            const baseDelay = initialDelayMs * Math.pow(2, attempt);
            const jitter = Math.random() * 0.3 * baseDelay;
            const delay = Math.min(baseDelay + jitter, maxDelayMs);

            console.warn(`Retrying ${operationName} after ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries}):`, {
                error: error.message,
                status: error.status || error.statusCode
            });

            await sleep(delay);
        }
    }
    throw lastError;
}
